import React from 'react';
import { DataIndicators } from '../DataIndicators';
import { sortObjectArrayNaturally } from '../../lib';
import { isDataLoaded, Status } from '../../types';
import { useSiteDevicesData } from '../../data/DataProviders/SiteDevicesDataProvider';
import { Box, Button, List, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { useHistory } from 'react-router-dom';
import { useDeviceCount } from '../../data/DataProviders/DeviceCountProvider';
import { FlatListItem } from '../FlatListItem/FlatListItem';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { MESH_TYPE } from '../../constants';
import { DataState, Data } from '../../types/data-providers';
import { getUpdatedDeviceStatusList } from '../../lib/signalr-helper';
import { DeviceVendor, OTAReleaseDetails, OtaUpgradeStatus } from '../../types/device';
import { useSignalRSiteData } from '../../data/DataProviders/SignalRSiteDataProvider';
import { Thunks } from '../../actions';
import { useDispatch } from 'react-redux';
import { getUserId, useAuthHelper, useAuthState } from '../AuthContextProvider';
import { useDeviceMaster } from '../../data/DataProviders/DeviceMasterProvider';

export const ConnectedDeviceList: React.FC<{ siteId: string }> = () => {
    const history = useHistory();
    const { addToast } = useToasts();
    const dispatch = useDispatch();
    const authHelper = useAuthHelper();
    const deviceCount = useDeviceCount();
    const siteDevicesData = useSiteDevicesData();
    const deviceMasterData = useDeviceMaster();
    const isEmpty = isDataLoaded(siteDevicesData) && siteDevicesData.value.length === 0;
    const connectDeviceCount = isDataLoaded(siteDevicesData) ? siteDevicesData.value.length : 0;
    const currentAppRoute = useCurrentAppRoute();
    const [pxdbdeviceList, setPxdbdeviceList] = React.useState<any[]>([]);
    const AddDeviceNavigation = (): void => {
        if (currentAppRoute.type === 'SiteProfile') {
            pushAppRoute(history, {
                type: 'AddDevice',
                orgId: currentAppRoute.orgId,
                locationId: currentAppRoute.locationId,
                siteId: currentAppRoute.siteId,
            });
        }
    };
    const authState = useAuthState();
    const userId = getUserId(authState) || '';
    const wellawareInfo = (): void => {
        addToast(
            'This Gateway and Devices cannot currently be managed through the Brightlayer Industrial UI. If you need to manage these Devices, please reach out to Brightlayer Support.',
            'error',
            'Functionality Not Supported'
        );
    };

    const updateStarfish = (
        deviceId: string,
        otaReleaseDetails: OTAReleaseDetails,
        otaUpgradeStatus: OtaUpgradeStatus
    ): void => {
        dispatch(
            Thunks.ManageAlarmsThunks.StarfishUpgrade(
                [deviceId],
                otaReleaseDetails,
                otaUpgradeStatus,
                userId,
                authHelper,
                addToast
            )
        );
        addToast('Gateway update started', 'error', 'Gateway Update');
    };

    const realTimeSiteData = useSignalRSiteData();
    const [deviceData, setdeviceData] = React.useState<Data<object | undefined>>({ state: DataState.Loading });
    const [isAddConnectedDeviceVisible, setIsAddConnectedDeviceVisible] = React.useState(true);

    React.useEffect(() => {
        if (isDataLoaded(siteDevicesData) && currentAppRoute.type === 'SiteProfile') {
            setdeviceData(siteDevicesData);
            deviceMasterData.getDevicesMasterData(currentAppRoute.orgId, 'pxdb-device-types');
        }
    }, [siteDevicesData, deviceData]);

    React.useEffect(() => {
        if (isDataLoaded(deviceMasterData.devicesData) && deviceMasterData.pxdbRowKey === 'pxdb-device-types') {
            setPxdbdeviceList(deviceMasterData.devicesData.value);
        }
    }, [deviceMasterData]);
    React.useEffect(() => {
        if (realTimeSiteData && realTimeSiteData.devices) {
            if (isDataLoaded(siteDevicesData)) {
                const clone = getUpdatedDeviceStatusList(realTimeSiteData.devices, siteDevicesData);
                setdeviceData(clone);
            }
        }
    }, [realTimeSiteData]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (isDataLoaded(siteDevicesData) && isDataLoaded(deviceMasterData.devicesData)) {
            siteDevicesData.value.map((device) =>
                device.type === MESH_TYPE ? setIsAddConnectedDeviceVisible(false) : ''
            );
            siteDevicesData.value.map(() => ({
                ...siteDevicesData.value,
                pxdbNavigation: false,
            }));

            siteDevicesData.value.map((y) => {
                pxdbdeviceList.map((m) => {
                    if (y.type === m.type) {
                        y.pxdbNavigation = true;
                    }
                });
                return siteDevicesData;
            });
        }
    }, [siteDevicesData, deviceMasterData.devicesData]);

    return (
        <>
            {connectDeviceCount === 0 && (
                <Box style={{ margin: 48, marginBottom: 0, marginLeft: 24 }}>
                    <Typography variant="h5" color="primary">
                        {' '}
                        {`Connected Devices (${connectDeviceCount})`}
                    </Typography>
                    <Typography variant="subtitle2"> {`0 Total Devices`}</Typography>
                </Box>
            )}
            <Paper style={{ marginTop: 24 }}>
                <DataIndicators
                    errorMessage={'No Connected Devices Found'}
                    emptyMessage={'No Connected Devices Found'}
                    data={siteDevicesData}
                    emptyIcon="Device"
                    isEmpty={isEmpty}
                    isDetailedInfo={false}
                    withAction={true}
                    description={
                        'Connected Devices are special type of Device that can connected directly to the Eaton Cloud through cellular or ethernet connection. Gateways are a common example of a Connected Device. '
                    }
                    action={
                        <Button
                            data-testid="add-device"
                            startIcon={<AddIcon fontSize="large" style={{ color: 'white' }} />}
                            color="primary"
                            onClick={(): void => {
                                AddDeviceNavigation();
                            }}
                            variant="contained"
                        >
                            Add Connected Device
                        </Button>
                    }
                />
            </Paper>
            {isDataLoaded(siteDevicesData) &&
                siteDevicesData.value.length > 0 &&
                isDataLoaded(deviceCount) &&
                currentAppRoute.type === 'SiteProfile' && (
                    <>
                        <Box display="flex" justifyContent="space-between" style={{ marginTop: 48 }}>
                            <Typography variant="h5" color="primary">
                                {`Connected Devices (${connectDeviceCount})`}
                            </Typography>
                            {isAddConnectedDeviceVisible && (
                                <Button
                                    startIcon={<AddIcon fontSize="large" style={{ color: 'white' }} />}
                                    color="primary"
                                    data-testid="add-device"
                                    onClick={(): void => {
                                        AddDeviceNavigation();
                                    }}
                                    variant="contained"
                                >
                                    Add Connected Device
                                </Button>
                            )}
                        </Box>

                        <Typography style={{ marginBottom: 24 }} variant="subtitle2">
                            {' '}
                            {`${deviceCount.value} Total Devices`}
                        </Typography>
                        <List style={{ padding: 0 }} data-testid="device-item">
                            {sortObjectArrayNaturally(siteDevicesData.value, [{ sortField: 'name' }]).map((device) => (
                                <FlatListItem
                                    key={device.id}
                                    item={{
                                        id: device.id,
                                        name: device.name,
                                        status: device.status ? Status.Online : Status.Offline,
                                        isEditable: device.isEditable,
                                        childDevicesExpected: device.childDevicesExpected,
                                        showChildDeviceStatus:
                                            device.areAllChildrenOnline !== null && !device.areAllChildrenOnline,
                                        vendor: device.vendor || '',
                                        type: device.type || '',
                                        otaReleaseDetails: device.otaReleaseDetails,
                                        otaUpgradeStatus: device.otaUpgradeStatus,
                                        pxdbNavigation: device.pxdbNavigation,
                                    }}
                                    infoClick={wellawareInfo}
                                    updateClick={updateStarfish}
                                    subTitleData={`${device.vendor || ''} ${device.type || ''}  | ${
                                        device.deviceCount
                                    } Devices`}
                                    hasStatus={true}
                                    navigate={{
                                        type: device.pxdbNavigation
                                            ? 'PxDeviceProfile'
                                            : device.type === MESH_TYPE
                                            ? 'MeshDeviceProfile'
                                            : device.type === DeviceVendor.SFStarfishGateway ||
                                              device.type === 'BLG_Brightlayer_Gateway_400'
                                            ? 'StarfishDeviceProfile'
                                            : 'DeviceProfile',
                                        orgId: currentAppRoute.orgId,
                                        locationId: currentAppRoute.locationId,
                                        siteId: currentAppRoute.siteId,
                                        deviceId: device.id,
                                    }}
                                ></FlatListItem>
                            ))}
                        </List>
                    </>
                )}
        </>
    );
};
